import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState } from "react";
import { SessionRecordFull } from "./Api";
import { BackendService, MessageRecord } from "./Backend"
import { useRouterContext } from "../router/routerContext";

type BackendContextType = {

    service: BackendService;
    initialised: boolean;
    loading: boolean;
    connected: boolean;
    isNewSession: boolean;
    session?: SessionRecordFull,

    connect: () => Promise<void>;

    submit: (
        line: string,
        imageData: string,
        colorA: string,
        colorB: string,
        comment: string
    ) => void;

}

const BackendContext = createContext<BackendContextType | null>(null);

export const BackendContextProvider: React.FC<React.PropsWithChildren<{
    serverUrl: string,
    apiSlug: string,
    clientSlug: string
}>> = props => {

    const id = useId();

    const { showStage } = useRouterContext();

    const service = useMemo(() => {
        return new BackendService(
            props.serverUrl,
            props.apiSlug,
            props.clientSlug
        );
    }, [props.serverUrl, props.apiSlug, props.clientSlug]);


    const [
        initialised,
        setInitialised
    ] = useState<boolean>(false);

    const [
        submitting,
        setSubmitting
    ] = useState<boolean>(false);


    const [
        loading,
        setLoading
    ] = useState<boolean>(service.api.loading);

    const [
        connected,
        setConnected
    ] = useState<boolean>(service.api.session !== undefined);

    const [
        isNewSession,
        setIsNewSession
    ] = useState<boolean>(service.api.hasBeenHereBefore === false);

    const [
        session,
        setSession
    ] = useState<SessionRecordFull | undefined>(service.api.session);


    /** Přidej listenery do služby */
    useEffect(() => {

        /** Synchronizuj sešnu */
        service.api.onSessionChanged.set(
            id,
            session => setSession(session)
        );

        /** Synchronizuj stav načítání */
        service.api.onLoadingChanged.set(
            id,
            loading => setLoading(loading)
        );


        return () => {
            service.api.onSessionChanged.delete(id);
            service.api.onLoadingChanged.delete(id);
        }

    }, [
        service,
        id,
        setSession,
        showStage
    ]);


    /** Volání připojení k Backendu */
    const connect = useCallback(async () => {

        const response = await service.connect();

        if (response) {
            setSession(response.existingSession);
            setIsNewSession(response.isNewSession);
            setConnected(true);
        }

        setInitialised(true);

    }, [
        service,
        setSession,
        setIsNewSession,
        setConnected,
        setInitialised
    ]);


    const submit = useCallback(async (
        line: string,
        imageData: string,
        colorA: string,
        colorB: string,
        comment: string
    ) => {

        setSubmitting(true);

        if (!submitting) {  

            const result = await service.submit(
                line,
                imageData,
                colorA,
                colorB,
                "branka"
            );

            if (result) {
                const message = service.messageFactory(
                    "Děkujeme za obrázek, ještě ho zkontrolujeme...",
                    "self",
                    5000,
                    true
                );
                // service.recieveMessage(message);
                showStage();
            } else {
                const message = service.messageFactory(
                    "Něco se pokazilo při odesílání Tvého obrázku, zkus to prosím znovu.",
                );
                service.recieveMessage(message);
            }

            setSubmitting( false );

        }





    }, [service, showStage, setSubmitting, submitting]);


    const value: BackendContextType = {
        service,
        initialised,
        loading,
        connected,
        isNewSession,
        session,
        connect,
        submit
    };

    return <BackendContext.Provider value={value}>
        {props.children}
    </BackendContext.Provider>

}

export const useBackendContext = () => {

    const context = useContext(BackendContext);

    if (context === null) {
        throw new Error("useBackendContext must be used within a BackendContextProvider");
    }

    return context;

}

/** Funkce pro obsluhu zpráv v Backend service */
export const useBackendContextMessages = () => {

    const { service } = useBackendContext();

    const id = useId();

    const [messages, setMessages] = useState<MessageRecord[]>([]);

    useEffect(() => {

        service.onMessageAdded.set(id, (
            addedMessage: MessageRecord,
            allMessages: MessageRecord[]
        ) => {

            setMessages([...allMessages]);

        });

        service.onMessageRemoved.set(id, (
            removedMessage: MessageRecord,
            allMessages: MessageRecord[]
        ) => {

            setMessages([...allMessages]);

        });


        return () => {
            service.onMessageAdded.delete(id);
            service.onMessageRemoved.delete(id);
        }

    }, [service, id, setMessages]);


    const clearAllMessages = useCallback(() => {
        service.forEveryMessage((message) => {
            service.removeMessage(message);
        });
    }, [service]);


    const addMessage = useCallback((
        text: string,
        closable: boolean,
        durationMs?: number,
        classes?: string[]
    ) => {

        const msg = service.messageFactory(
            text,
            "UI",
            durationMs,
            closable,
            classes
        )

        service.recieveMessage(msg)

    }, [service]);

    return {
        messages,
        clearAllMessages,
        addMessage
    }

}


/** Zkratka pro zavření zprávy */
export const useMessageClose = (
    message: MessageRecord
) => {

    return useCallback(() => {

        if (message.closable) {
            message.service.removeMessage(message);
        }

    }, [message]);

}